import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { FormLayoutContent, FormLayoutFooter, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import UtimacoPacketSwitchForm from 'routes/UtimacoPacketSwitch/components/UtimacoPacketSwitchForm';
import { Ref } from 'routes/UtimacoPacketSwitch/components/UtimacoPacketSwitchForm/UtimacoPacketSwitchForm';
import { RootState } from 'data/root.reducers';
import * as actions from 'data/utimacoPacketSwitch/utimacoPacketSwitch.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as accessFunctionSelectors from 'data/accessFunction/accessFunction.selectors';
import * as collectionFunctionSelectors from 'data/collectionFunction/collectionFunction.selectors';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import { newUtimacoPacketSwitch } from 'routes/UtimacoPacketSwitch/UtimacoPacketSwitch.types';
import { adaptToCreate } from 'data/utimacoPacketSwitch/utimacoPacketSwitch.adapter';
import { UtimacoPacketSwitch } from 'data/utimacoPacketSwitch/utimacoPacketSwitch.types';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormTemplateSection } from 'data/template/template.types';
import { utimacoPacketSwitchTemplate } from '../../templates';
import { adaptTemplateVisibilityMode, adaptTemplateOptions, getLeaOptions } from '../../utils/UtimacoPacketSwitch.adapter';
import { getAuditDetailsToSubmit } from '../../utils/utimacoPacketSwitchDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  utimacoPacketSwitchTemplateDef: adaptTemplateVisibilityMode(utimacoPacketSwitchTemplate, 'create'),
  deliveryFunctionId: state.global.selectedDF,
  userId: state.authentication.userId,
  utimacoPacketSwitchTemplate: adaptTemplateVisibilityMode(utimacoPacketSwitchTemplate, 'create'),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  accessFunctions: accessFunctionSelectors.getAccessFunctions(state),
  collectionFunctions: collectionFunctionSelectors.getCollectionFunctionList(state),
  collectionFunctionGroups: collectionFunctionGroupSelectors.getCollectionFunctionGroupList(state),
});

const mapDispatch = {
  createUtimacoPacketSwitch: actions.createUtimacoPacketSwitch,
  goToUtimacoPacketSwitchList: actions.goToUtimacoPacketSwitchList,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>
export type Props = PropsFromRedux & PropsFromRouter;

export const UtimacoPacketSwitchCreate = ({
  history,
  userId,
  deliveryFunctionId,
  userName,
  isAuditEnabled,
  utimacoPacketSwitchTemplateDef,
  accessFunctions,
  collectionFunctions,
  collectionFunctionGroups,
  goToUtimacoPacketSwitchList,
  createUtimacoPacketSwitch,
}: Props): JSX.Element => {
  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<UtimacoPacketSwitch>(cloneDeep(newUtimacoPacketSwitch));
  const [template, setTemplate] = useState(utimacoPacketSwitchTemplateDef);
  const { t } = useTranslation();

  useEffect(() => {
    setTemplate(
      adaptTemplateOptions(
        template,
        accessFunctions,
        collectionFunctions,
        collectionFunctionGroups,
      ),
    );
  }, [
    accessFunctions,
    collectionFunctions,
    collectionFunctionGroups,
  ]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (template == null) {
      return;
    }

    const clonedFormData = cloneDeep(formData);
    const clonedTemplate = cloneDeep(template);

    if (fieldName === 'AFID') {
      (clonedTemplate.main as FormTemplateSection).fields.LEA.options = getLeaOptions(
        value,
        accessFunctions,
      );
    }

    const newData = {
      ...clonedFormData,
      [fieldName]: value,
    };

    clonedTemplate.key = Date.now().toString();
    // clonedTemplate.main = adaptNetworkIdField(template.main as FormTemplateSection, newData);

    setFormData(newData);
    setTemplate(clonedTemplate);
  };

  const getCreateUtimacoPacketSwitchPayload = () => {
    if (formRef.current === undefined || formRef.current === null) return null;

    if (!formRef.current.isValid(true, true)) {
      return null;
    }

    const distributorConfigFormData = formRef.current.getValues();
    const payload = {
      data: adaptToCreate(distributorConfigFormData),
      auditDetails: getAuditDetailsToSubmit(distributorConfigFormData, null, isAuditEnabled, userId, userName, 'CREATE'),
    };

    return payload;

  };

  const handleClickSave = () => {
    const payload = getCreateUtimacoPacketSwitchPayload();
    if (payload) {
      createUtimacoPacketSwitch(deliveryFunctionId, payload, history);
    }
  };

  const handleCancel = () => goToUtimacoPacketSwitchList(history);

  const renderForm = () => {
    if (formData && template) {
      return (
        <UtimacoPacketSwitchForm
          ref={formRef}
          data={formData}
          template={template}
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleCancel}
          onClickSave={handleClickSave}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="UtimacoPacketSwitchCreate">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('utimacoPacketSwitch.create.title'))}
          {renderForm()}
        </FormLayoutContent>
        <FormLayoutFooter>
          {renderFormPanel('')}
        </FormLayoutFooter>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(UtimacoPacketSwitchCreate));
